import { DemoType } from '@repo/shared/dtos/organization'
import { Button } from '@repo/ui/button'
import { Card, CardContent } from '@repo/ui/card'
import { H2 } from '@repo/ui/h2'
import { useT } from '@repo/ui/hooks'
import { sortByKey } from '@repo/ui/utils'
import { Building2, Home, RotateCcw, Scale } from 'lucide-react'
import { useCallback } from 'react'
import { Link } from 'react-router-dom'

import { Logo } from '@/components/layouts/Logo'
import { demoStoreActions } from '@/store/actions'
import { OrganizationDemo } from '@/store/demo'
import { useStoreDispatch, useStoreState } from '@/store/hooks'

export function Demo({ organizations }: { organizations: OrganizationDemo[] }) {
  const dispatch = useStoreDispatch()
  const { t } = useT()
  const { resettingOrganizationId } = useStoreState((state) => state.demo)
  const sortedOrganizations = [...organizations].sort(sortByKey('name'))

  const handleReset = useCallback(
    ({ organizationId }: { organizationId: string }) => {
      // The store will handle setting and clearing the resettingOrganizationId
      void dispatch(demoStoreActions.resetDemo({ organizationId }))
    },
    [dispatch],
  )

  return (
    <div className="relative flex flex-1 flex-col">
      <div className="bg-primary-50 flex w-full items-center">
        <div className="ml-4 flex flex-1">
          <Link to="/" className="text-primary my-6 flex items-center justify-center gap-4">
            <Logo isWhite={false} />
          </Link>
        </div>
      </div>
      <div className="mx-auto mt-12 flex w-full flex-col gap-4 px-6 xl:w-1/2 xl:px-0">
        <H2 className="text-primary flex items-center gap-2">{t('pages.demo.title')}</H2>
        <Card className="flex w-full flex-col">
          <CardContent className="flex flex-1 flex-col gap-6 pt-4">
            {sortedOrganizations.length > 0 ? (
              <ul className="flex flex-1 flex-col">
                {sortedOrganizations.map((org) => (
                  <li
                    key={org.id}
                    className="text-primary-600 flex items-center justify-between border-b border-gray-100 py-2 last:border-0"
                  >
                    <OrgHandler organization={org} />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReset({ organizationId: org.id })}
                      disabled={resettingOrganizationId === org.id}
                      aria-label={
                        resettingOrganizationId === org.id
                          ? t('pages.demo.resetting')
                          : t('pages.demo.reset')
                      }
                    >
                      {resettingOrganizationId === org.id ? (
                        <span
                          className="flex items-center gap-1"
                          data-testid={`resetting-${org.id}`}
                        >
                          <RotateCcw className="h-3 w-3 animate-spin" />
                          {t('pages.demo.resetting')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <RotateCcw className="h-3 w-3" />
                          {t('pages.demo.reset')}
                        </span>
                      )}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-8 text-center text-gray-500">{t('pages.demo.empty')}</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function OrgHandler({ organization }: { organization: OrganizationDemo }) {
  const { t } = useT()
  return (
    <div className="flex items-center gap-2">
      {(() => {
        // Select icon based on demo key
        if (!organization.key) {
          return <Building2 className="text-primary-300 size-5" />
        }

        switch (organization.key) {
          case DemoType.POVERTY:
            return <Home className="text-primary-300 size-5" />
          case DemoType.JUSTICE:
            return <Scale className="text-primary-300 size-5" />
          default:
            return <Building2 className="text-primary-300 size-5" />
        }
      })()}
      <div className="flex flex-col">
        <span className="font-semibold">
          {organization.name}
          {organization.key && (
            <span className="text-primary-500 ml-2 text-sm font-normal">
              ({organization.key}&nbsp;|&nbsp;
              {organization.language})
            </span>
          )}
        </span>
        <span className="text-sm text-gray-500">
          {t('pages.demo.features')}: {organization.key ? t('features.chat') : ''}
        </span>
      </div>
    </div>
  )
}
